// EMAIL-CRM-2026-10-22: the Email button and its compose box -- for a lead, a facility request, an alert row. It replaces
// every mailto: in the CRM. The email goes from Matt through crm-mailer (lib/email.js): Reply-To Matt, his signature
// added by the mailer, the subject and the message exactly as shown. One send per click; the outcome is logged on the
// timeline ("Email · <subject> · Sent" / "Bounced"). A lead marked No email gets a disabled button and the reason.
import React, { useRef, useState } from 'react';
import TemplatePicker from './TemplatePicker';
import { unresolved } from './lib/templates';
import { SUBJECT_MAX, BODY_MAX, NO_EMAIL_WHY, FROM_LINE, EMAIL_ERRORS, checkEmail } from './lib/email';

const btn = (bg, fg, off) => ({ background: bg, color: fg, borderRadius: 8, padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700, border: 0,
  cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.55 : 1, display: 'inline-flex', alignItems: 'center' });
const field = { display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, fontSize: 14, fontFamily: 'inherit', padding: 10,
  border: '1px solid #cbd5e1', borderRadius: 8 };

/** The Email button. No address -> off ("No email on file"); opted out -> "No email", off, the reason on hover. */
export function EmailButton({ email, optedOut = false, onOpen, testid = 'lead-email', style = null }) {
  const has = !!String(email || '').trim();
  const off = !has || optedOut;
  const why = !has ? 'No email on file' : optedOut ? NO_EMAIL_WHY : undefined;
  return (
    <button className="nb" type="button" data-testid={testid} disabled={off} title={why} aria-label={optedOut ? 'No email' : undefined}
      onClick={() => { if (!off) onOpen(); }} style={style || btn(off ? '#f1f5f9' : '#e2e8f0', off ? '#64748b' : '#0f172a', off)}>
      {optedOut ? 'No email' : 'Email'}
    </button>
  );
}

/** "No email" said once, where the address is shown. */
export function NoEmailNote({ testid = 'no-email-note' }) {
  return <span data-testid={testid} className="pill" title={NO_EMAIL_WHY} style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700 }}>No email</span>;
}

/**
 * to: the address; who: the name for the heading; leadId / candidateId: whose timeline it goes on;
 * subject, body: prefilled; send(payload) -> Promise (lib/email.js makeEmailApi().send); onSent(result); onBounced();
 * optedOut: nothing can be sent (the box says why).
 */
// NIGHT-DIRECTSTAFF-2026-10-25 §3: templateCtx -> "Start from an approved template" (subject and body filled, merges applied; a
// placeholder left unfilled blocks Send)
export default function EmailCompose({ to, who = '', leadId = null, candidateId = null, subject: subject0 = '', body: body0 = '', optedOut = false,
  send, onClose, onSent = () => {}, onBounced = () => {}, templateCtx = null }) {
  const [subject, setSubject] = useState(subject0);
  const [body, setBody] = useState(body0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');
  const [bounced, setBounced] = useState(optedOut);
  const inFlight = useRef(false);
  const name = who || to || 'this person';
  const go = async () => {
    if (inFlight.current || done || bounced) return;
    const bad = checkEmail({ to, kind: 'message', subject, body });
    if (bad) { setError(EMAIL_ERRORS[bad]); return; }
    inFlight.current = true;
    setBusy(true); setError('');
    try {
      const r = await send({ to, kind: 'message', subject: subject.trim(), body: body.trim(), leadId, candidateId });
      // NIGHT-DIRECTSTAFF-2026-10-22 §6: offline, the email is kept on this device and goes once when the connection is back
      setDone(r && r.queued ? 'Saved: sending when online.' : r && r.logged === false ? 'Emailed. (It could not be added to the timeline.)' : 'Emailed. It is on the timeline.');
      onSent(r);
    } catch (e) {
      setError((e && e.message) || EMAIL_ERRORS.send_failed);
      if (e && e.code === 'recipient_suppressed') { setBounced(true); onBounced(); }
    } finally { setBusy(false); inFlight.current = false; }
  };
  const off = busy || !!done || bounced || !subject.trim() || !body.trim() || unresolved(`${subject}\n${body}`).length > 0;
  return (
    <div data-testid="email-compose-overlay" role="dialog" aria-modal="true" aria-label={`Email ${name}`}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 50, display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', padding: 12, boxSizing: 'border-box', overflowY: 'auto' }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div data-testid="email-compose" className="card" style={{ width: '100%', maxWidth: 520, boxSizing: 'border-box', padding: 16, background: 'white', borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: 0, overflowWrap: 'anywhere' }}>Email {name}</h3>
          <button className="nb" type="button" data-testid="email-close" onClick={onClose} disabled={busy} style={btn('#f1f5f9', '#475569', busy)}>Close</button>
        </div>
        <div data-testid="email-to" style={{ fontSize: 13, color: '#334155', marginTop: 4, overflowWrap: 'anywhere' }}>To {to}</div>
        <div data-testid="email-from" style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{FROM_LINE}</div>
        {bounced && <p data-testid="email-no-email" role="alert" style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, margin: '10px 0 0' }}>{NO_EMAIL_WHY}</p>}
        {templateCtx && !done && !bounced && <TemplatePicker ctx={templateCtx} channel="email" subject={subject} preview={body}
          onPick={(t) => { setSubject(t.subject || subject); setBody(t.body); setError(''); }} />}
        <label htmlFor="email-subject" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginTop: 12 }}>Subject</label>
        <input id="email-subject" data-testid="email-subject" value={subject} maxLength={SUBJECT_MAX} disabled={bounced || !!done}
          onChange={(e) => { setSubject(e.target.value); setError(''); }} style={{ ...field, minHeight: 44 }} />
        <label htmlFor="email-body" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginTop: 10 }}>Message (sent as shown)</label>
        <textarea id="email-body" data-testid="email-body" value={body} rows={7} maxLength={BODY_MAX.message} disabled={bounced || !!done}
          onChange={(e) => { setBody(e.target.value); setError(''); }} style={field} />
        <div style={{ fontSize: 11, color: '#475569', textAlign: 'right' }}>{body.length}/{BODY_MAX.message}</div>
        {error && <div data-testid="email-error" role="alert" style={{ marginTop: 8, fontSize: 13, color: '#b91c1c', overflowWrap: 'anywhere' }}>{error}</div>}
        {done && <div data-testid="email-done" role="status" style={{ marginTop: 8, fontSize: 13, color: '#166534', fontWeight: 600 }}>{done}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="nb" type="button" data-testid="email-send" onClick={go} disabled={off} style={btn('#1d4ed8', 'white', off)}>
            {busy ? 'Sending…' : done ? 'Emailed ✓' : 'Send email'}</button>
        </div>
      </div>
    </div>
  );
}
